import { z } from 'zod';
import { StreakSchema, TestResultSchema } from './common';

export const MAX_CODE_LENGTH = 50_000;
export const MAX_HINTS = 3;
export const MAX_DURATION_MS = 86_400_000;

const attemptFields = {
  contentVersion: z.number().int().min(1),
  results: z.array(TestResultSchema).min(1).max(50),
  passed: z.boolean(),
  hintsUsed: z.number().int().min(0).max(MAX_HINTS),
  durationMs: z.number().int().min(0).max(MAX_DURATION_MS),
};

const passedMatchesResults = (v: { passed: boolean; results: { passed: boolean }[] }) =>
  v.passed === v.results.every((r) => r.passed);
const passedMismatch = { message: 'Must equal whether every result passed', path: ['passed'] };

export const SubmitAttemptRequestSchema = z
  .strictObject({
    ...attemptFields,
    code: z.string().max(MAX_CODE_LENGTH, 'Must be 50,000 characters or fewer').nullable().optional(),
  })
  .refine(passedMatchesResults, passedMismatch);
export type SubmitAttemptRequest = z.infer<typeof SubmitAttemptRequestSchema>;

export const UnlockedItemSchema = z.object({
  kind: z.enum(['challenge', 'chapter']),
  id: z.string(),
});

export const SubmitAttemptResponseSchema = z.object({
  attemptId: z.uuid(),
  passed: z.boolean(),
  firstPass: z.boolean(),
  xpAwarded: z.number().int().min(0),
  totalXp: z.number().int().min(0),
  streak: StreakSchema,
  unlocked: z.array(UnlockedItemSchema),
});
export type SubmitAttemptResponse = z.infer<typeof SubmitAttemptResponseSchema>;

/** A guest attempt as kept in localStorage and sent to `POST /progress/import`. */
export const GuestAttemptSchema = z
  .strictObject({
    challengeId: z.string().regex(/^[a-z0-9-]{1,80}$/),
    ...attemptFields,
    at: z.iso.datetime({ offset: true }),
  })
  .refine(passedMatchesResults, passedMismatch);
export type GuestAttempt = z.infer<typeof GuestAttemptSchema>;

export const MAX_GUEST_ATTEMPTS = 20;

export const ImportProgressRequestSchema = z.strictObject({
  attempts: z.array(GuestAttemptSchema).min(1).max(MAX_GUEST_ATTEMPTS),
});
export type ImportProgressRequest = z.infer<typeof ImportProgressRequestSchema>;

export const ImportProgressResponseSchema = z.object({
  imported: z.number().int().min(0),
  skipped: z.number().int().min(0),
  xpAwarded: z.number().int().min(0),
});
export type ImportProgressResponse = z.infer<typeof ImportProgressResponseSchema>;
